import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import WhySection from './components/WhySection';
import CategoriesSection from './components/CategoriesSection';
import BuyingGuidesSection from './components/BuyingGuidesSection';
import ComparisonSection from './components/ComparisonSection';
import SafetySection from './components/SafetySection';
import TestimonialSection from './components/TestimonialSection';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import ArticlesSection from './components/ArticlesSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://kakobuyspreadsheet.cam';

export default function Home() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'KakobuySpreadsheet',
      url: SITE_URL,
      description: 'Discover trusted Kakobuy Spreadsheet links, seller comparisons, buying guides, and safe shopping tips.',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    });
    document.head.appendChild(script);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'What is a Kakobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'A Kakobuy Spreadsheet is a curated, regularly updated document that organizes product listings from verified sellers across multiple categories. It helps buyers quickly locate trusted sellers, compare prices, and discover trending items without manually searching through unorganized platforms.' } },
        { '@type': 'Question', name: 'How often is the kakobuy spreadsheet updated?', acceptedAnswer: { '@type': 'Answer', text: 'Our kakobuyspreadsheet is refreshed daily. Links are checked for availability, seller ratings are recalculated based on the latest community feedback, and new trending items are added every 24 hours.' } },
        { '@type': 'Question', name: 'Is the Kakobuy Spreadsheet free to use?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, the kakobuy spreadsheet resource is completely free. We believe transparent, organized buying information should be accessible to everyone. Simply browse the categories and click through to explore listings.' } },
        { '@type': 'Question', name: 'How do I know the sellers in the spreadsheet are trustworthy?', acceptedAnswer: { '@type': 'Answer', text: 'Every seller listed in the best kakobuy spreadsheet undergoes our multi-step verification process: community feedback analysis, link health monitoring, and periodic manual review.' } },
        { '@type': 'Question', name: 'Can I use the kakobuy spreadsheet on mobile?', acceptedAnswer: { '@type': 'Answer', text: 'Absolutely. The kakobuyspreadsheet website is fully responsive and optimized for mobile browsing. Category pages load quickly, and the dropdown navigation works smoothly on any screen size.' } },
        { '@type': 'Question', name: 'What categories are available in the kakobuy spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'The kakobuy spreadsheet covers 14 major categories including Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff.' } },
        { '@type': 'Question', name: 'How do I report a broken link or suspicious seller?', acceptedAnswer: { '@type': 'Answer', text: 'Visit our main platform at kakobuyspreadsheet and use the contact options. Our team reviews reports within 24 hours and updates the spreadsheet accordingly.' } },
        { '@type': 'Question', name: 'What makes this the best kakobuy spreadsheet resource?', acceptedAnswer: { '@type': 'Answer', text: 'Unlike static lists, our kakobuy spreadsheet combines real-time link monitoring, community-driven seller ratings, and editorial buying guides. This three-layer system ensures accuracy, freshness, and buyer safety.' } },
        { '@type': 'Question', name: 'Do you offer buying guides for specific categories?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Our buying guides section covers Shoes, Bags, Accessories, and Clothing in detail. Each guide includes pricing trends, sizing tips, and seller comparison data.' } },
        { '@type': 'Question', name: 'How do I get started with the Kakobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Simply browse the category dropdown at the top of this page, select your preferred category, and you will be directed to the corresponding kakobuy spreadsheet listings on our main platform.' } },
      ],
    });
    document.head.appendChild(faqScript);

    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'KakobuySpreadsheet',
      url: SITE_URL,
      description: 'Independent buying resource helping users discover trusted spreadsheet links, seller guides, and shopping information.',
      foundingDate: '2026',
      logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
    });
    document.head.appendChild(orgScript);

    const bcScript = document.createElement('script');
    bcScript.type = 'application/ld+json';
    bcScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'KakobuySpreadsheet',
          item: SITE_URL,
        },
      ],
    });
    document.head.appendChild(bcScript);

    return () => {
      if (script.parentNode) document.head.removeChild(script);
      if (faqScript.parentNode) document.head.removeChild(faqScript);
      if (orgScript.parentNode) document.head.removeChild(orgScript);
      if (bcScript.parentNode) document.head.removeChild(bcScript);
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <WhySection />
        <CategoriesSection />
        <BuyingGuidesSection />
        <ArticlesSection />
        <ComparisonSection />
        <SafetySection />
        <TestimonialSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </>
  );
}